import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Facebook, Linkedin, Instagram } from 'lucide-react';
import { companyInfo } from '../data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    services: [
      { name: 'CSCS Cards', path: '/cscs-services' },
      { name: 'NVQ Courses', path: '/nvq-courses' },
      { name: 'CPCS Training', path: '/cpcs-courses' },
      { name: 'Site Management', path: '/site-management' }
    ],
    company: [
      { name: 'About Us', path: '/about' },
      { name: 'Contact', path: '/contact' },
      { name: 'FAQ', path: '/faq' },
      { name: 'Privacy Policy', path: '/privacy' }
    ],
    cards: [
      { name: 'Green CSCS Card', path: '/green-labour-card' },
      { name: 'Blue CSCS Card', path: '/cscs-services' },
      { name: 'Gold CSCS Card', path: '/cscs-services' },
      { name: 'Black CSCS Card', path: '/cscs-services' }
    ]
  };

  return (
    <footer className="bg-construction-darkblue text-white">
      {/* Main Footer Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <img 
              src="/logo.png" 
              alt="Construction Cards Helpline" 
              className="h-16 w-auto object-contain mb-4"
            />
            <p className="text-gray-300 mb-4 text-sm leading-relaxed">
              Your trusted partner for CSCS cards, NVQ qualifications, and construction training across the UK.
            </p>
            <div className="space-y-3">
              <a 
                href={`tel:${companyInfo.phone}`}
                className="flex items-center gap-2 text-construction-yellow hover:text-yellow-400 transition-colors font-semibold"
              >
                <Phone size={18} />
                <span>{companyInfo.phone}</span>
              </a>
              <a 
                href={`mailto:${companyInfo.email}`}
                className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors text-sm"
              >
                <Mail size={18} />
                <span>{companyInfo.email}</span>
              </a>
              <div className="flex items-start gap-2 text-gray-300 text-sm">
                <MapPin size={18} className="mt-0.5 flex-shrink-0" />
                <span>{companyInfo.address}</span>
              </div>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-bold mb-4 text-construction-yellow">Our Services</h4>
            <ul className="space-y-2">
              {footerLinks.services.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-gray-300 hover:text-construction-yellow transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* CSCS Cards */}
          <div>
            <h4 className="text-lg font-bold mb-4 text-construction-yellow">CSCS Cards</h4>
            <ul className="space-y-2">
              {footerLinks.cards.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.path}
                    className="text-gray-300 hover:text-construction-yellow transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company & Hours */}
          <div>
            <h4 className="text-lg font-bold mb-4 text-construction-yellow">Company</h4>
            <ul className="space-y-2 mb-6">
              {footerLinks.company.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-gray-300 hover:text-construction-yellow transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="border-t border-gray-600 pt-4">
              <div className="flex items-start gap-2 text-gray-300 text-sm mb-2">
                <Clock size={18} className="mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-white mb-1">Business Hours</p>
                  <p className="whitespace-pre-line leading-relaxed">{companyInfo.businessHours}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Social Media & Trust Badges */}
        <div className="mt-12 pt-8 border-t border-gray-700">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            {/* Social Media */}
            <div className="flex items-center gap-4">
              <span className="text-gray-400 text-sm">Follow Us:</span>
              <div className="flex gap-3">
                <a 
                  href="https://www.facebook.com/ConstructionCardsHelpline" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-gray-700 p-2 rounded-full hover:bg-construction-yellow hover:text-construction-darkblue transition-colors"
                  aria-label="Facebook"
                >
                  <Facebook size={18} />
                </a>
                <a 
                  href="https://www.instagram.com/ConstructionCardsHelpline" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-gray-700 p-2 rounded-full hover:bg-construction-yellow hover:text-construction-darkblue transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram size={18} />
                </a>
                <a 
                  href="https://www.linkedin.com/company/construction-cards-helpline-limited/posts/?feedView=all" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-gray-700 p-2 rounded-full hover:bg-construction-yellow hover:text-construction-darkblue transition-colors"
                  aria-label="LinkedIn"
                >
                  <Linkedin size={18} />
                </a>
              </div>
            </div>

            {/* Trust Indicators */}
            <div className="flex items-center gap-6 text-gray-400 text-sm">
              <div className="text-center">
                <p className="font-bold text-white text-lg">UK</p>
                <p className="text-xs">Registered</p>
              </div>
              <div className="text-center">
                <p className="font-bold text-white text-lg">5★</p>
                <p className="text-xs">Rated</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-black/30 py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-400">
            <p>© {currentYear} {companyInfo.name}. All rights reserved.</p>
            <div className="flex gap-6">
              <Link to="/terms" className="hover:text-construction-yellow transition-colors">
                Terms & Conditions
              </Link>
              <Link to="/privacy" className="hover:text-construction-yellow transition-colors">
                Privacy Policy
              </Link>
              <Link to="/cookies" className="hover:text-construction-yellow transition-colors">
                Cookie Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
